import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Badge,
  Tooltip,
  Box,
  Divider,
  CircularProgress,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  TextField,
  InputAdornment,
  Pagination,
  Chip,
} from "@mui/material";
import { Add, Edit, Delete, PinDrop, Report, Search, Clear } from "@mui/icons-material";
import BlogDialog from "./BlogDialog";
import "./blog.scss";
import { ApiPost, ApiGet, ApiDelete } from "../../../helpers/API/ApiData";
import debounce from "lodash/debounce"; // For debouncing search input

const Blog = () => {
  const isAdmin = true; // Replace with actual auth logic
  const [posts, setPosts] = useState([]);
  const [totalPosts, setTotalPosts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPostId, setCurrentPostId] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletePostId, setDeletePostId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    page: 1,
    limit: 7,
  });

  // Debounced search handler
  const debouncedFetchPosts = useCallback(
    debounce(() => {
      fetchPosts();
    }, 500),
    [filters]
  );

  // Fetch posts when filters change
  useEffect(() => {
    debouncedFetchPosts();
    return () => debouncedFetchPosts.cancel(); // Cleanup debounce on unmount
  }, [filters, debouncedFetchPosts]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        limit: filters.limit,
        page: filters.page,
        postType: "article",
        ...(filters.search && { search: filters.search }),
      }).toString();

      const res = await ApiGet(`post/posts?${queryParams}`);
      if (res.data?.result === 0) {
        setPosts(res.data.payload.posts || []);
        setTotalPosts(res.data.payload.total || 0);
      } else {
        console.error("Failed to fetch posts:", res.data.message);
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (post = null) => {
    setIsEditing(!!post);
    setCurrentPostId(post ? post._id : null);
    setOpenDialog(true);
  };

  const handleSubmit = async (data) => {
    try {
      const endpoint = isEditing ? `post/post/${currentPostId}` : "post/post";
      const res = await ApiPost(endpoint, data);
      console.log(isEditing ? "Post updated:" : "Post created:", res);
      setOpenDialog(false);
      fetchPosts();
    } catch (error) {
      console.error(`Error ${isEditing ? "updating" : "creating"} post:`, error);
      throw error;
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setIsEditing(false);
    setCurrentPostId(null);
  };

  const handleOpenDeleteConfirm = (postId) => {
    setDeletePostId(postId);
    setDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setDeletePostId(null);
    setErrorMessage("");
    setIsDeleting(false);
  };

  const handleDeletePost = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    setErrorMessage("");
    try {
      const res = await ApiDelete(`post/post/${deletePostId}`);
      if (res.data?.result === 0) {
        console.log("Post deleted:", res);
        fetchPosts();
        handleCloseDeleteConfirm();
      } else {
        setErrorMessage(res.data.message || "Failed to delete post");
        setIsDeleting(false);
      }
    } catch (error) {
      console.error("Error deleting post:", error);
      setErrorMessage("Failed to delete post. Please try again.");
      setIsDeleting(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      page: 1, // Reset to first page on search change
    }));
  };

  const handlePageChange = (event, value) => {
    setFilters((prev) => ({
      ...prev,
      page: value,
    }));
  };

  const handleClearSearch = () => {
    setFilters((prev) => ({
      ...prev,
      search: "",
      page: 1,
    }));
  };

  return (
    <div className="blog-page">
      <Container className="blog-container">
        {/* Header Section */}
        <Box className="blog-header" sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h3" className="blog-title">
            Blog Hub
          </Typography>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              className="add-blog-button"
              aria-label="Create new post"
            >
              Create Post
            </Button>
          )}
        </Box>

        {/* Search Section */}
        <Box sx={{ mb: 4, bgcolor: "background.paper", borderRadius: 2, boxShadow: 1 }}>
          <TextField
            fullWidth
            label="Search Posts"
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: filters.search && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClearSearch}
                    aria-label="Clear search"
                  >
                    <Clear />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            className="search-input"
            aria-label="Search posts by title or content"
            placeholder="Search by title or content..."
          />
        </Box>

        {/* Create/Edit Dialog */}
        <BlogDialog
          open={openDialog}
          onClose={handleCloseDialog}
          initialData={isEditing ? posts.find((p) => p._id === currentPostId) : null}
          isEditing={isEditing}
          onSubmit={handleSubmit}
        />

        {/* Delete Confirm Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={isDeleting ? undefined : handleCloseDeleteConfirm}
          TransitionComponent={Fade}
          transitionDuration={300}
          aria-labelledby="delete-dialog-title"
        >
          <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
          <DialogContent>
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
            {isDeleting ? (
              <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column" gap={2}>
                <CircularProgress size={30} />
                <Typography>Deleting post...</Typography>
              </Box>
            ) : (
              <Typography>Are you sure you want to delete this post? This action cannot be undone.</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseDeleteConfirm}
              color="primary"
              disabled={isDeleting}
              aria-label="Cancel delete"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeletePost}
              color="error"
              variant="contained"
              disabled={isDeleting}
              aria-label="Confirm delete"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Posts Section */}
        <Typography variant="h5" className="posts-section-title" gutterBottom>
          ✨ All Posts
        </Typography>
        <Divider className="section-divider" />

        {loading ? (
          <Box className="loading-container">
            <CircularProgress className="loading-spinner" />
            <Typography variant="body1" className="loading-text">
              Loading posts...
            </Typography>
          </Box>
        ) : posts.length > 0 ? (
          <>
            <Grid container spacing={3} className="posts-grid">
              {posts.map((post) => (
                <Grid item xs={12} sm={6} md={4} key={post._id}>
                  <Card className={`post-card ${post.isPinned ? "pinned" : ""}`} elevation={3}>
                    {post.media?.[0]?.url && (
                      <CardMedia
                        component="img"
                        height="200"
                        image={post.media[0].url}
                        alt={post.title || "Post"}
                        className="post-image"
                      />
                    )}
                    <CardContent className="post-content">
                      <Box className="post-top-icons">
                        {post.isPinned && (
                          <Tooltip title="Pinned Post">
                            <PinDrop className="pinned-icon" />
                          </Tooltip>
                        )}
                        {post.moderation?.isReported && (
                          <Tooltip title={`Reported (${post.moderation.reportCount})`}>
                            <Badge badgeContent={post.moderation.reportCount} color="error">
                              <Report className="reported-icon" />
                            </Badge>
                          </Tooltip>
                        )}
                      </Box>
                      {post.postType === "article" && post.title && (
                        <Typography variant="h6" className="post-title" gutterBottom>
                          {post.title}
                        </Typography>
                      )}
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                        <Chip
                          size="small"
                          label={post.section ? post.section[0].toUpperCase() + post.section.slice(1) : "Needs section review"}
                          color={post.section ? "primary" : "warning"}
                          variant="outlined"
                        />
                        <Chip size="small" label={post.status || "draft"} />
                        <Chip
                          size="small"
                          label={Array.isArray(post.channels) && post.channels.includes("leaderbridge")
                            ? "LeaderBridge Ideas"
                            : "Needs destination review"}
                          color={Array.isArray(post.channels) && post.channels.includes("leaderbridge") ? "default" : "warning"}
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="body2" className="post-content-text">
                        {post.content.replace(/<[^>]+>/g, "").substring(0, 120)}...
                      </Typography>
                      {post.location && (
                        <Typography variant="caption" className="post-location">
                          📍 {post.location}
                        </Typography>
                      )}
                      <Typography variant="caption" className="post-meta">
                        By <strong>{post.userId?.name || "Anonymous"}</strong> |{" "}
                        {new Date(post.createdAt).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </Typography>
                      <Box className="post-stats">
                        <Typography variant="caption">
                          👀 {post.viewCount || 0} | 📈 {post.impressionCount || 0} | 🔗{" "}
                          {post.clickCount || 0}
                        </Typography>
                      </Box>
                      {isAdmin && (
                        <Box className="post-actions">
                          <Tooltip title="Edit Post">
                            <IconButton
                              onClick={() => handleOpenDialog(post)}
                              className="edit-button"
                              aria-label={`Edit post ${post.title || "untitled"}`}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Post">
                            <IconButton
                              onClick={() => handleOpenDeleteConfirm(post._id)}
                              className="edit-button"
                              aria-label={`Delete post ${post.title || "untitled"}`}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Pagination
                count={Math.ceil(totalPosts / filters.limit)}
                page={filters.page}
                onChange={handlePageChange}
                color="primary"
                aria-label="Pagination for blog posts"
              />
            </Box>
          </>
        ) : (
          <Fade in timeout={500}>
            <Box className="no-posts">
              <Typography variant="h6" className="no-posts-title">
                No Posts Yet
              </Typography>
              <Typography variant="body2" className="no-posts-text">
                Start by creating your first post!
              </Typography>
              {isAdmin && (
                <Button
                  variant="outlined"
                  onClick={() => handleOpenDialog()}
                  className="no-posts-button"
                  aria-label="Create first post"
                >
                  Create Your First Post
                </Button>
              )}
            </Box>
          </Fade>
        )}
      </Container>
    </div>
  );
};

export default Blog;
